import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import dayjs from 'dayjs';
import { ResourceApi, ResourceDayDTO, ResourceDTO } from '@lkp/parking-sdk';
import { RESOURCE_API } from '../../../core/providers/api.module';
import { LineChartDataset } from 'src/app/shared/statistics/models';
import { Subject, takeUntil } from 'rxjs';
import { DateService } from 'src/app/core/services/date.service';
import { ExportService } from 'src/app/core/services/export.service';

@Component({
    selector: 'app-occupancy-report',
    templateUrl: './occupancy-report.component.html',
    styleUrls: ['./occupancy-report.component.scss'],
    standalone: false
})
export class OccupancyReportComponent implements OnInit {
  from = new FormControl<Date | undefined>(dayjs().hour(0).minute(0).toDate());
  to = new FormControl<Date | undefined>(
    dayjs().add(1, 'week').hour(0).minute(0).toDate()
  );
  activeResourceId?: number;
  errorMessage = false;

  chartTitles: string[] = [];
  chartData: LineChartDataset[] = [];
  loading = true;
  occupancyReports: ResourceDayDTO[] = [];

  resources: ResourceDTO[] = [];

  readonly onDestroy$ = new Subject<void>();

  constructor(
    @Inject(RESOURCE_API) readonly resourceApi: ResourceApi,
    readonly exportService: ExportService,
    readonly dateService: DateService
  ) {}

  ngOnInit(): void {
    this.getOccupancyReport();

    /* Listen to changes */
    this.from.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      if (this.to.value) {
        this.to.setValue(undefined, {
          emitEvent: false,
        });
      }
      this.from.value && this.to?.value && this.getOccupancyReport();
    });

    this.to?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () => this.from.value && this.to?.value && this.getOccupancyReport()
      );

    /* Get resources */
    this.resourceApi
      .getResources()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          this.resources = data;
          if (this.resources.length) {
            this.activeResourceId = this.resources[0].resourceId;
          }
        },
        error: () => {
          console.error('Error fetching resources');
        },
      });
  }

  changeResourceType(value: number): void {
    this.activeResourceId = value;
    this.getOccupancyReport();
  }

  getOccupancyReport(): void {
    if (this.from.value && this.to.value) {
      this.loading = true;
      this.chartData = [];
      this.chartTitles = [];

      this.resourceApi
        .getResourceStatusAdmin({
          fromDate: dayjs(this.from.value).second(0).millisecond(0).toDate(),
          toDate: dayjs(this.to.value)
            .second(0)
            .millisecond(0)
            .add(1, 'day')
            .toDate(),
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (data) => {
            this.occupancyReports = data;
            let arrivalData: number[] = [],
              departureData: number[] = [],
              totalData: number[] = [];

            data.forEach((val) => {
              // for each object key
              const allocations = val?.allocations;
              const date = val?.date;
              if (allocations && date) {
                Object.keys(allocations).forEach(() => {
                  this.chartTitles.push(this.formatDate(date, 'd MMM'));
                  arrivalData.push(
                    allocations[this.activeResourceId || 0].arrivalQty || 0
                  );
                  departureData.push(
                    allocations[this.activeResourceId || 0].departureQty || 0
                  );
                  totalData.push(
                    allocations[this.activeResourceId || 0].occupiedQty || 0
                  );
                });
              }
            });

            this.chartData.push({
              data: arrivalData,
              name: 'Ankommande',
              type: 'line',
            });

            this.chartData.push({
              data: departureData,
              name: 'Avgående',
              type: 'line',
            });

            this.chartData.push({
              data: totalData,
              name: 'Fyllda platser',
              type: 'line',
            });

            this.loading = false;
            this.errorMessage = false;
          },
          error: (error) => {
            console.error('Error fetching occupancy report: ', error);
            this.errorMessage = true;
          },
        });
    }
  }

  formatDate(date: Date | string, format: string) {
    return this.dateService.formatDateWithDay(date);
  }

  downloadReportBookings() {
    if (this.from.value && this.to.value)
      this.exportService.getReportBookings(
        dayjs(this.from.value).second(0).millisecond(0).toDate(),
        dayjs(this.to.value).second(0).millisecond(0).add(1, 'day').toDate()
      );
  }

  getResourceLabels(): string[] {
    const resourceLabels = this.resources.map(
      (resource) => resource.label || ''
    );
    return ['empty-header', ...resourceLabels];
  }

  getResourceLabel(): string {
    let resource = undefined;
    const activeResource = this.activeResourceId;
    if (activeResource) {
      resource = this.resources.find(
        (resource) => resource.resourceId === +activeResource
      );
    }
    return resource?.label || '';
  }

  getColumnDefinitions(): string[] {
    const columnDefinitions: string[] = this.resources.flatMap((resource) => {
      return [
        `arriving-${resource.name}`,
        `departing-${resource.name}`,
        `filled-${resource.name}`,
      ];
    });
    return ['date', ...columnDefinitions];
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
